import { ListActionTarget } from './ListActionTarget';
import { Orderlist } from './OrderList';
import { PriceTier } from './PriceTier';
import { ACTION_ADD, ACTION_COPY, ACTION_MOVE } from '../js/store/constants';
import { UpdateProductOptions } from '../js/store/products/actionsCart';
import { SubscriptionPeriodType } from './Article';

export type AddToListOptions = {
    action: typeof ACTION_ADD;
    target: typeof ListActionTarget.List;
    list: Orderlist;
    priceTier: PriceTier;
    subscriptionPeriodType: SubscriptionPeriodType;
};

export type AddToCartOptions = {
    action: typeof ACTION_ADD;
    target: typeof ListActionTarget.Cart;
    priceTier: PriceTier;
    subscriptionPeriodType: SubscriptionPeriodType;
};

export type CopyToListOptions = {
    action: typeof ACTION_COPY;
    target: typeof ListActionTarget.List;
    list: Orderlist;
};

export type CopyToCartOptions = {
    action: typeof ACTION_COPY;
    target: typeof ListActionTarget.Cart;
};

export type MoveToListOptions = {
    action: typeof ACTION_MOVE;
    target: typeof ListActionTarget.List;
    list: Orderlist;
};

export type TopChartAddToListOptions = AddToListOptions & {
    readonly includeAllProducts: boolean;
    readonly subscriptionPeriodType: SubscriptionPeriodType;
};

export type TopChartAddToCartOptions = AddToCartOptions & {
    readonly includeAllProducts: boolean;
    readonly subscriptionPeriodType: SubscriptionPeriodType;
};

export type IsbnAddToListOptions = {
    isbns: string;
    action: typeof ACTION_ADD;
    target: typeof ListActionTarget.List;
    listId: string;
};

export type IsbnAddToCartOptions = {
    isbns: string;
    action: typeof ACTION_ADD;
    target: typeof ListActionTarget.Cart;
};

export const isAddToListOptions = (
    options: CopyToCartOptions | CopyToListOptions | MoveToListOptions | AddToListOptions | AddToCartOptions
): options is AddToListOptions => {
    return options.action === ACTION_ADD && options.target === ListActionTarget.List;
};

export const isAddToCartOptions = (
    options: CopyToCartOptions | CopyToListOptions | MoveToListOptions | AddToListOptions | AddToCartOptions
): options is AddToCartOptions => {
    return options.action === ACTION_ADD && options.target === ListActionTarget.Cart;
};

export const isCopyToListOptions = (
    options: CopyToCartOptions | CopyToListOptions | MoveToListOptions | AddToListOptions | AddToCartOptions
): options is CopyToListOptions => {
    return options.action === ACTION_COPY && options.target === ListActionTarget.List;
};
export const isCopyToCartOptions = (
    options: CopyToCartOptions | CopyToListOptions | MoveToListOptions | AddToListOptions | AddToCartOptions
): options is CopyToCartOptions => {
    return options.action === ACTION_COPY && options.target === ListActionTarget.Cart;
};

export const isMoveOptions = (
    options: CopyToCartOptions | CopyToListOptions | MoveToListOptions | AddToListOptions | AddToCartOptions
): options is MoveToListOptions => {
    return options.action === ACTION_MOVE && options.target === ListActionTarget.List;
};

export const isTopChartAddToListOptions = (
    options: TopChartAddToListOptions | TopChartAddToCartOptions
): options is TopChartAddToListOptions => {
    return 'includeAllProducts' in options && isAddToListOptions(options);
};

export const isIsbnAddToListOptions = (
    options: IsbnAddToListOptions | CopyToCartOptions
): options is IsbnAddToListOptions => {
    return 'isbns' in options && !('priceTier' in options);
};

export const isIsbnAddToCartOptions = (
    options: CopyToListOptions | MoveToListOptions | IsbnAddToCartOptions
): options is IsbnAddToCartOptions => {
    return 'isbns' in options && !('priceTier' in options);
};

export type SingleAddOptions = (
    | { readonly target: typeof ListActionTarget.Cart }
    | { readonly target: typeof ListActionTarget.List; readonly listId: string }
) & {
    readonly options: UpdateProductOptions;
};

export type SingleCopyOrMoveOptions = SingleAddOptions & {
    action: typeof ACTION_COPY | typeof ACTION_MOVE;
};

export const isSingleCopyOptions = (
    options: SingleAddOptions | SingleCopyOrMoveOptions
): options is SingleCopyOrMoveOptions => {
    return 'action' in options && options.action === ACTION_COPY;
};

export const isSingleMoveOptions = (
    options: SingleAddOptions | SingleCopyOrMoveOptions
): options is SingleCopyOrMoveOptions => {
    return 'action' in options && options.action === ACTION_MOVE;
};

export type RenewalAddOptions = (
    | { readonly target: typeof ListActionTarget.Cart }
    | { readonly target: typeof ListActionTarget.List; readonly listId: string }
) & {
    readonly totalValue: number;
};
